// ArticleA1.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";

const THEME = {
  fontFamily:
    "'Prompt', system-ui, -apple-system, Segoe UI, Roboto, 'Helvetica Neue', Arial, 'Noto Sans Thai', sans-serif",
  pageBg: "#0b1220",
  textOnDark: "rgba(255,255,255,.92)",
  glass: "rgba(255,255,255,.08)",
  glassStroke: "rgba(255,255,255,.18)",
  primary: "#60a5fa",
  primary2: "#22d3ee",
};

const META = {
  id: "a1",
  title: "คู่มือการติดตามทวงหนี้อย่างถูกกฎหมาย (อัปเดต 2025)",
  category: "ทวงหนี้",
  dateISO: "2025-08-22",
  readMinsFallback: 8,
  hero:
    "https://images.unsplash.com/photo-1450101499163-c8848c66ca85?q=80&w=2000&auto=format&fit=crop",
  excerpt:
    "ขั้นตอน สิทธิ และข้อห้ามที่ลูกหนี้–เจ้าหนี้ต้องรู้ พร้อมเช็กลิสต์เอกสารก่อนเริ่มดำเนินการ",
};

export default function ArticleA1() {
  const [progress, setProgress] = useState(0);
  const [activeId, setActiveId] = useState("");
  const [readMins, setReadMins] = useState(META.readMinsFallback);
  const contentRef = useRef(null);
  const heroRef = useRef(null);

  // progress bar
  useEffect(() => {
    const onScroll = () => {
      const el = contentRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const total = el.scrollHeight - window.innerHeight * 0.6;
      const scrolled = Math.min(
        Math.max(window.scrollY - (el.offsetTop - window.innerHeight * 0.4), 0),
        total
      );
      setProgress(total > 0 ? (scrolled / total) * 100 : 0);

      // hero parallax/blur
      const h = heroRef.current;
      if (h) {
        const y = window.scrollY;
        h.style.setProperty("--parallax", Math.min(y * 0.22, 120) + "px");
        h.style.setProperty("--blur", Math.min(y / 70, 8) + "px");
        h.style.setProperty("--fade", Math.min(y / 420, 0.5));
      }
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // compute reading time (rough)
  useEffect(() => {
    const el = contentRef.current;
    if (!el) return;
    const text = el.innerText || "";
    const words = text.trim().split(/\s+/).length;
    // ไทยอ่านเฉลี่ย ~200 wpm (mixed th/en)
    setReadMins(Math.max(4, Math.round(words / 200)));
  }, []);

  // ScrollSpy for TOC
  useEffect(() => {
    const heads = document.querySelectorAll("main article h2[id], main article h3[id]");
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) setActiveId(e.target.id);
        });
      },
      { rootMargin: "0px 0px -70% 0px", threshold: 0.01 }
    );
    heads.forEach((h) => io.observe(h));
    return () => io.disconnect();
  }, []);

  const url = useMemo(
    () => (typeof window !== "undefined" ? window.location.href : "/articles/a1"),
    []
  );

  const share = (type) => {
    const text = encodeURIComponent(META.title);
    const u = encodeURIComponent(url);
    let shareUrl = "";
    if (type === "fb") shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${u}`;
    if (type === "tw") shareUrl = `https://twitter.com/intent/tweet?url=${u}&text=${text}`;
    if (type === "ln") shareUrl = `https://social-plugins.line.me/lineit/share?url=${u}`;
    window.open(shareUrl, "_blank", "noopener,noreferrer,width=680,height=520");
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(url);
      alert("คัดลอกลิงก์แล้ว");
    } catch {
      alert("คัดลอกไม่สำเร็จ");
    }
  };

  const copyTemplate = () => {
    const t = document.getElementById("demand-letter-template")?.innerText || "";
    navigator.clipboard
      .writeText(t)
      .then(() => alert("คัดลอกตัวอย่างหนังสือทวงถามแล้ว"))
      .catch(() => alert("คัดลอกไม่สำเร็จ"));
  };

  const fmtDate = (iso) =>
    new Date(iso).toLocaleDateString("th-TH", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });

  return (
    <div className="article-page" style={{ fontFamily: THEME.fontFamily }}>
      <Style />
      <ReadingProgress value={progress} />

      {/* JSON-LD */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{
        __html: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Article",
          headline: META.title,
          datePublished: META.dateISO,
          dateModified: META.dateISO,
          author: { "@type": "Person", name: "ทีมทนายความ Thai Tax & Law" },
          image: META.hero,
          articleSection: META.category,
          description: META.excerpt,
          mainEntityOfPage: url,
        }),
      }} />

      {/* HERO */}
      <header className="hero" ref={heroRef}>
        <img className="hero-media" src={META.hero} alt="" />
        <div className="hero-overlay" />
        <div className="hero-content">
          <nav className="breadcrumbs">
            <a href="/">หน้าแรก</a> <span>•</span> <a href="/articles">บทความ</a>{" "}
            <span>•</span> <span aria-current="page">{META.category}</span>
          </nav>
          <h1 className="hero-title">{META.title}</h1>
          <p className="hero-excerpt">{META.excerpt}</p>
          <div className="hero-meta">
            <span>{fmtDate(META.dateISO)}</span>
            <span>•</span>
            <span>{readMins} นาที</span>
            <span className="cat">{META.category}</span>
          </div>
        </div>
        <div className="hero-bottom-fade" />
      </header>

      {/* BODY */}
      <div className="containerr">
        {/* TOOLS */}
        <aside className="side tools">
          <div className="tool-card">
            <div className="tool-title">สารบัญ</div>
            <nav className="toc">
              {[
                ["intro", "ภาพรวม & ข้อชี้แจงสำคัญ"],
                ["law", "กฎหมายที่เกี่ยวข้อง"],
                ["docs", "เช็กลิสต์เอกสารก่อนเริ่ม"],
                ["timeline", "ไทม์ไลน์การติดตาม"],
                ["prohibited", "ข้อห้ามตามกฎหมายทวงถามหนี้"],
                ["letter", "ตัวอย่างหนังสือทวงถาม (ก๊อปได้)"],
                ["restruct", "การเจรจา/โครงสร้างหนี้"],
                ["whenlawyer", "เมื่อไรควรส่งให้ทนายความ"],
                ["faq", "FAQ คำถามพบบ่อย"],
              ].map(([id, label]) => (
                <a
                  key={id}
                  href={`#${id}`}
                  className={activeId === id ? "active" : ""}
                  onClick={(e) => {
                e.preventDefault();
                const el = document.getElementById(id);
                if (el) {
                    const yOffset = -80; // ชดเชย navbar 80px
                    const y = el.getBoundingClientRect().top + window.pageYOffset + yOffset;
                    window.scrollTo({ top: y, behavior: "smooth" });
                }
                }}

                >
                  {label}
                </a>
              ))}
            </nav>
          </div>

          {/* <div className="tool-card share" >
            <div className="tool-title">แชร์</div>
            <div className="share-row">
              <button onClick={() => share("fb")} aria-label="แชร์ Facebook">
                <svg viewBox="0 0 24 24"><path fill="currentColor" d="M13 22v-9h3l1-4h-4V7a2 2 0 0 1 2-2h2V1h-3a5 5 0 0 0-5 5v3H6v4h3v9z"/></svg>
                Facebook
              </button>
              <button onClick={() => share("tw")} aria-label="แชร์ X/Twitter">
                <svg viewBox="0 0 24 24"><path fill="currentColor" d="M3 3l8.1 10.3L3 21h3.4l6.1-6.8L17.6 21H21l-8.2-10.5L21 3h-3.3l-5.9 6.6L7 3H3z"/></svg>
                X
              </button>
              <button onClick={() => share("ln")} aria-label="แชร์ LINE">
                <svg viewBox="0 0 24 24"><path fill="currentColor" d="M20.1 4.1A10.6 10.6 0 0 0 12 1.7C6.5 1.7 2 5.5 2 10c0 4 3.5 7.4 8.2 8.2.6.1.5.4.5.7l-.1 2.3c0 .7.5.6.7.5l2.9-1.6c.2-.1.4-.2.7-.1A10 10 0 0 0 22 10c0-2.3-.9-4.2-1.9-5.9zM8.6 12.6H7V7.9h1.6v4.7zm3.6 0H10V7.9h1.6v3.1l2-3.1h1.6l-1.9 2.9 2.1 3.8h-1.6l-1.4-2.7-1.2 1.7v1z"/></svg>
                LINE
              </button>
              <button onClick={copyLink} aria-label="คัดลอกลิงก์">
                <svg viewBox="0 0 24 24"><path fill="currentColor" d="M3 12a5 5 0 0 1 5-5h4v2H8a3 3 0 1 0 0 6h4v2H8a5 5 0 0 1-5-5zm8-3h5a5 5 0 1 1 0 10h-5v-2h5a3 3 0 1 0 0-6h-5V9z"/></svg>
                คัดลอกลิงก์
              </button>
            </div>
          </div> */}

          {/* <div className="tool-card author" >
            <div className="tool-title">ผู้เขียน</div>
            <div className="author-row">
              <img
                src="https://images.unsplash.com/photo-1544723795-3fb6469f5b39?q=80&w=300&auto=format&fit=crop"
                alt=""
              />
              <div>
                <div className="name">ทีมทนายความ Thai Tax & Law</div>
                <div className="role">ทวงหนี้ / ครอบครัว / ภาษี</div>
              </div>
            </div>
            <div className="author-meta">
              เผยแพร่ {fmtDate(META.dateISO)} • {readMins} นาที
            </div>
          </div> */}
        </aside>

        {/* MAIN */}
        <main>
          <article ref={contentRef}>
            <p className="lede">
              บทความนี้ออกแบบให้เป็น <b>เช็กลิสต์ใช้งานได้จริง</b> สำหรับเจ้าหนี้และ
              ธุรกิจที่ต้องติดตามหนี้ค้างชำระ โดยคงคุณภาพตามกฎหมายไทยและ{" "}
              <b>ไม่ละเมิด พ.ร.บ.การทวงถามหนี้ พ.ศ. 2558</b>
            </p>

            <Callout type="info">
              บทความนี้ให้ข้อมูลทั่วไป ไม่ใช่คำปรึกษากฎหมายส่วนบุคคล
              เคสของคุณอาจมีรายละเอียดเฉพาะ—หากมูลหนี้สูง/มีความเสี่ยงด้านชื่อเสียง
              แนะนำให้ <a href="/contact">ปรึกษาทนายความ</a> ก่อนเริ่มดำเนินการ
            </Callout>

            <h2 id="intro">ภาพรวม & ข้อชี้แจงสำคัญ</h2>
            <ul>
              <li>
                เป้าหมาย: <b>ปิดหนี้เร็วที่สุด</b> โดยต้นทุนต่ำและไม่กระทบความสัมพันธ์
              </li>
              <li>หลัก: เก็บหลักฐานทุกสเต็ป (บันทึกการติดต่อ, ไทม์ไลน์, เอกสาร)</li>
              <li>
                สื่อสารแบบมืออาชีพ: รักษาน้ำเสียง สุภาพ ชัดเจน และให้ทางเลือกชำระหนี้
              </li>
              <li>
                ข้อห้าม: หลีกเลี่ยงการเปิดเผยหนี้ต่อบุคคลที่สาม/คุกคาม/กดดันเกินสมควร
              </li>
            </ul>

            <h2 id="law">กฎหมายที่เกี่ยวข้อง</h2>
            <ul>
              <li>
                <b>พ.ร.บ.การทวงถามหนี้ พ.ศ. 2558</b>: กำหนด “วิธีการ” และ “ข้อห้าม”
                ของผู้ทวงถามหนี้ (รวมถึงผู้รับมอบหมาย)
              </li>
              <li>
                <b>ประมวลกฎหมายแพ่งและพาณิชย์</b>: สิทธิเรียกร้อง, ดอกเบี้ยผิดนัด,
                อายุความ
              </li>
              <li>
                <b>ประมวลกฎหมายวิธีพิจารณาความแพ่ง</b>: ขั้นตอนฟ้องคดี/บังคับคดี
              </li>
            </ul>

            <h2 id="docs">เช็กลิสต์เอกสารก่อนเริ่ม</h2>
            <div className="grid-2">
              <ul className="check">
                <li>สัญญา/ใบสั่งซื้อ/ใบเสนอราคา/ใบรับสินค้า</li>
                <li>ใบกำกับภาษี/ใบแจ้งหนี้/ใบลดหนี้ (ถ้ามี)</li>
                <li>หลักฐานการส่งมอบ/บริการแล้วเสร็จ</li>
                <li>ประวัติการชำระเงิน/โอน/เช็ค/สลิป</li>
              </ul>
              <ul className="check">
                <li>ข้อมูลติดต่อที่ยืนยันแล้ว (เบอร์/อีเมล/ที่อยู่)</li>
                <li>ผู้มีอำนาจตัดสินใจ/ผู้อนุมัติชำระ</li>
                <li>ประวัติการติดต่อครั้งก่อน (ถ้ามี)</li>
                <li>สรุปยอดหนี้ ณ วันที่ทวง + ดอกเบี้ย (ถ้ามี)</li>
              </ul>
            </div>

            <Callout type="tip">
              จัดเก็บทุกอย่างเป็น <b>แฟ้มเดียว</b> และตั้งชื่อไฟล์มาตรฐาน
              (YYYYMMDD_ชื่อลูกค้า_หัวข้อ.pdf) ช่วยให้กระบวนการเร็วขึ้นอย่างมาก
            </Callout>

            <h2 id="timeline">ไทม์ไลน์การติดตาม</h2>
            <ol className="steps">
              <li>
                <b>เตือนอ่อน (Friendly Reminder)</b> — ภายใน 3–7 วันหลังครบกำหนด:
                แจ้งยอด/วันครบกำหนด/ช่องทางชำระ
              </li>
              <li>
                <b>ทวงถามอย่างเป็นทางการ</b> — โทร/อีเมลผู้มีอำนาจ พร้อมระบุเส้นตาย
                และตัวเลือกผ่อนชำระ
              </li>
              <li>
                <b>หนังสือทวงถาม (Demand Letter)</b> — ส่งไปรษณีย์ลงทะเบียน/อีเมลบริษัท
                แนบเอกสารยืนยันยอด พร้อมให้เวลา 7–14 วัน
              </li>
              <li>
                <b>นัดเจรจา</b> — ตกลงโครงสร้างหนี้ (ดูส่วนถัดไป)
              </li>
              <li>
                <b>เตือนก่อนส่งทนายความ</b> — แจ้งเส้นตายสุดท้าย/ผลทางกฎหมาย
              </li>
              <li>
                <b>ส่งให้ทนายความ/ฟ้องคดี</b> — เมื่อหลักฐานพร้อมและการเจรจาไม่คืบหน้า
              </li>
            </ol>

            <h2 id="prohibited">ข้อห้ามตามกฎหมายทวงถามหนี้</h2>
            <div className="grid-2">
              <ul className="no">
                <li>ติดต่อในเวลาไม่เหมาะสม/ถี่จนเดือดร้อน</li>
                <li>เปิดเผยหนี้ต่อบุคคลที่สาม/ที่ทำงาน</li>
                <li>ข่มขู่ ดูหมิ่น หรือใช้คำหยาบ</li>
                <li>ติดป้าย/โพสต์ประจานต่อสาธารณะ</li>
              </ul>
              <ul className="no">
                <li>เรียกค่าใช้จ่ายเกินจริง/ไม่มีสิทธิ</li>
                <li>แอบอ้างเป็นเจ้าหน้าที่รัฐ/ศาล/ตำรวจ</li>
                <li>ให้ข้อมูลเท็จหรือทำให้เข้าใจผิด</li>
                <li>ติดต่อผู้เยาว์/ผู้ไร้ความสามารถโดยไม่มีผู้ปกครอง</li>
              </ul>
            </div>

            <h2 id="letter">ตัวอย่างหนังสือทวงถาม (ก๊อปได้)</h2>
            <div className="template">
              <pre id="demand-letter-template">{`หัวข้อ: หนังสือทวงถามชำระหนี้

เรียน  {{ชื่อลูกค้า/บริษัท}} 
ที่อยู่  {{ที่อยู่}}

ตามที่ท่านได้ค้างชำระค่าสินค้า/บริการตามใบแจ้งหนี้เลขที่ {{เลขที่บิล}} ลงวันที่ {{วันที่}} 
เป็นเงินทั้งสิ้น {{ยอดเงิน}} บาท ({{ตัวหนังสือ}}) กำหนดชำระ {{วันครบกำหนด}}

เพื่อให้การดำเนินงานเป็นไปด้วยดี โปรดชำระภายใน {{X}} วันนับแต่ได้รับหนังสือนี้ 
โดยชำระผ่าน {{ช่องทางชำระ}} และแจ้งหลักฐานมายัง {{อีเมล/ไลน์/โทร}}

หากพ้นกำหนดดังกล่าว บริษัทจำเป็นต้องดำเนินการตามสิทธิตามกฎหมาย 
รวมถึงการมอบหมายทนายความเพื่อดำเนินคดีโดยไม่แจ้งให้ทราบล่วงหน้า

ขอแสดงความนับถือ
{{ชื่อผู้มีอำนาจ}}  ตำแหน่ง {{ตำแหน่ง}}
บริษัท {{ชื่อบริษัท}}    โทร {{เบอร์}}    อีเมล {{อีเมล}}`}</pre>
              <div className="template-actions">
                <button onClick={copyTemplate}>
                  <svg viewBox="0 0 24 24"><path d="M16 1H4a2 2 0 0 0-2 2v12h2V3h12V1zm3 4H8a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h11a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2zm0 16H8V7h11v14z" fill="currentColor"/></svg>
                  คัดลอกแบบฟอร์ม
                </button>
                <a className="ghost" href="/contact">อยากให้เราปรับเป็นกรณีของคุณ</a>
              </div>
            </div>

            <h2 id="restruct">การเจรจา/โครงสร้างหนี้</h2>
            <div className="grid-2">
              <div>
                <h3>ตัวเลือกที่ใช้ได้ผล</h3>
                <ul>
                  <li>ผ่อนชำระแบบคงที่ (เช่น 6–12 งวด)</li>
                  <li>ส่วนลดโปะปิด (ลดเมื่อจ่ายภายใน X วัน)</li>
                  <li>เลื่อนชำระชั่วคราวพร้อมแผนฟื้นสภาพคล่อง</li>
                  <li>ดอกเบี้ยตามกฎหมาย/ตามสัญญาที่ไม่เกินเพดาน</li>
                </ul>
              </div>
              <div>
                <h3>สิ่งที่ต้องระวัง</h3>
                <ul>
                  <li>ทำเอกสารยืนยันข้อตกลงใหม่ ทุกครั้ง</li>
                  <li>ระบุเหตุผิดนัด/ยกเลิกส่วนลดหากไม่ปฏิบัติตาม</li>
                  <li>กำหนดผู้ติดต่อและวิธีส่งหลักฐานชำระที่ชัดเจน</li>
                </ul>
              </div>
            </div>

            <h2 id="whenlawyer">เมื่อไรควรส่งให้ทนายความ</h2>
            <ul>
              <li>ยอดหนี้สูง/มีโอกาสสูญเสียลูกหนี้</li>
              <li>ลูกหนี้หยุดสื่อสาร/เลื่อนนัดซ้ำ</li>
              <li>มีข้อพิพาทข้อเท็จจริง/ข้อกฎหมาย</li>
              <li>ต้องการดำเนินคดี/บังคับคดีอย่างเป็นระบบ</li>
            </ul>

            <h2 id="faq">FAQ คำถามพบบ่อย</h2>
            <details>
              <summary>โทรทวงได้กี่ครั้งต่อวัน/ช่วงเวลาไหน?</summary>
              <p>
                ควรติดต่ออย่างเหมาะสม หลีกเลี่ยงเวลาพักผ่อน/กลางคืน
                และไม่ถี่จนรบกวน—ยึดหลักสุจริตและจำเป็นเท่านั้น
              </p>
            </details>
            <details>
              <summary>เรียกเก็บ “ค่าติดตามทวงถาม” เพิ่มได้ไหม?</summary>
              <p>ทำได้เฉพาะที่มีสิทธิตามสัญญาหรือกฎหมายกำหนดและต้องเหมาะสม</p>
            </details>
            <details>
              <summary>เผยแพร่รายชื่อลูกหนี้ในสื่อสังคมได้หรือไม่?</summary>
              <p>ไม่ได้ ถือเป็นการเปิดเผยหนี้ต่อสาธารณะและอาจผิดกฎหมาย</p>
            </details>
            <details>
              <summary>คำนวณดอกเบี้ยผิดนัดยังไง?</summary>
              <p>กรณีไม่มีระบุในสัญญา ใช้อัตราตามกฎหมาย—ควรปรึกษาผู้เชี่ยวชาญก่อนแจ้ง</p>
            </details>

            <Callout type="success">
              ต้องการทีมช่วยทั้งกระบวนการ? เรามี <b>แพ็กเกจติดตามหนี้แบบมืออาชีพ</b>{" "}
              ตั้งแต่ตรวจหลักฐาน → ทำหนังสือทวงถาม → เจรจา → ฟ้องคดี
              <a href="/contact"> นัดคุยฟรี 15 นาที</a>
            </Callout>

            <div className="end-actions">
              <a className="btn-primary" href="/contact">
                ปรึกษาทนายความตอนนี้
                <svg viewBox="0 0 24 24" width="18" height="18">
                  <path d="M5 12h12M13 5l7 7-7 7" stroke="currentColor" fill="none" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </a>
              <a
                className="ghost"
                href="#top"
                onClick={(e) => {
                  e.preventDefault();
                  window.scrollTo({ top: 0, behavior: "smooth" });
                }}
              >
                กลับขึ้นบน
              </a>
            </div>
          </article>

          {/* RELATED */}
          <section className="related">
            <h3>อ่านต่อ</h3>
            <div className="rel-grid">
              <a className="rel-card" href="/articles/a5">
                <img src="https://images.unsplash.com/photo-1520607162513-77705c0f0d4a?q=80&w=1200&auto=format&fit=crop" alt="" />
                <div className="meta">
                  <span className="cat">ทวงหนี้</span>
                  <div className="title">Checklist ทวงหนี้สำหรับธุรกิจบริการรายเดือน</div>
                </div>
              </a>
              <a className="rel-card" href="/articles/a2">
                <img src="https://www.lawdepot.com/images/lawLibrary/Articles/are-divorce-records-public.jpg" alt="" />
                <div className="meta">
                  <span className="cat">ครอบครัว</span>
                  <div className="title">วางแผนทรัพย์สินก่อนการหย่า: ปกป้องสิทธิอย่างมืออาชีพ</div>
                </div>
              </a>
              <a className="rel-card" href="/articles/a4">
                <img src="https://images.unsplash.com/photo-1542621334-a254cf47733d?q=80&w=1200&auto=format&fit=crop" alt="" />
                <div className="meta">
                  <span className="cat">ภาษี</span>
                  <div className="title">ภาษีหนี้สูญ: ตัดหนี้ลูกหนี้เบี้ยวให้ถูกต้อง</div>
                </div>
              </a>
            </div>
          </section>
        </main>
      </div>
    </div>
  );
}

/* ---------- Components ---------- */
function ReadingProgress({ value }) {
  return (
    <div className="progress-wrap" aria-hidden="true">
      <div className="progress" style={{ width: `${value}%` }} />
    </div>
  );
}

function Callout({ type = "info", children }) {
  return <div className={`callout ${type}`}>{children}</div>;
}

/* ---------- Styles ---------- */
function Style() {
  return (
    <style>{`
:root{
  --bg:${THEME.pageBg};
  --text:${THEME.textOnDark};
  --glass:${THEME.glass};
  --glass-stroke:${THEME.glassStroke};
  --p1:${THEME.primary};
  --p2:${THEME.primary2};
}
*{box-sizing:border-box}
.article-page{
  color:var(--text);
  background:
    radial-gradient(1200px 600px at 10% -10%, rgba(14,165,233,.12), transparent 60%),
    radial-gradient(1200px 600px at 90% 10%, rgba(99,102,241,.12), transparent 60%),
    var(--bg);
  min-height:100vh;
}

/* progress bar */
.progress-wrap{position:fixed;top:0;left:0;right:0;height:3px;z-index:100}
.progress{
  height:3px;background:linear-gradient(90deg,var(--p1),var(--p2));
  box-shadow:0 0 16px rgba(34,211,238,.35);
}

/* hero */
.hero{position:relative;height:58vh;overflow:hidden;isolation:isolate;--parallax:0px;--blur:0px;--fade:0}
.hero-media{position:absolute;inset:-10% -10% -10% -10%;width:120%;height:120%;object-fit:cover;
  transform:translateY(var(--parallax));filter:blur(var(--blur)) brightness(.85) saturate(.95)}
.hero-overlay{position:absolute;inset:0;background:
  radial-gradient(700px 380px at 18% 32%, rgba(0,0,0,.45), rgba(0,0,0,.7)),
  linear-gradient(90deg, rgba(0,0,0,.6), rgba(0,0,0,.25), rgba(0,0,0,.6));opacity:calc(1 - var(--fade)*.35)}
.hero-bottom-fade{position:absolute;left:0;right:0;bottom:-1px;height:120px;background:linear-gradient(to bottom, transparent, var(--bg));z-index:2}
.hero-content{position:relative;z-index:3;height:100%;display:grid;align-content:center;gap:14px;
  padding: clamp(20px,5vw,64px);max-width:1100px;margin:0 auto}
.breadcrumbs{opacity:.9;font-size:.95rem}
.breadcrumbs a{color:rgba(255,255,255,.9);text-decoration:none}
.breadcrumbs span{opacity:.6;margin:0 6px}
.hero-title{font-size: clamp(2rem, 4.2vw, 3.6rem);line-height:1.1;margin:0;font-weight:800}
.hero-excerpt{max-width:60ch;opacity:.95}
.hero-meta{display:flex;gap:8px;align-items:center;opacity:.9}
.hero-meta .cat{margin-left:8px;padding:6px 10px;border-radius:999px;background:rgba(255,255,255,.12);border:1px solid rgba(255,255,255,.2)}

/* layout */
.containerr{display:grid;grid-template-columns: 300px 1fr;gap:24px;max-width:1200px;margin:0 auto;padding: 0 clamp(16px,4vw,24px)}
.side{padding-top:28px}
main{padding-top:28px}

/* tools */
.tool-card{position:sticky;top:84px;background:var(--glass);border:1px solid var(--glass-stroke);backdrop-filter: blur(8px);
  border-radius:16px;padding:14px 14px 12px;margin-bottom:14px}
.tool-title{font-weight:800;margin-bottom:8px}
.toc{display:grid;gap:8px}
.toc a{padding:8px 10px;border-radius:12px;text-decoration:none;color:var(--text);border:1px solid transparent}
.toc a:hover{background:rgba(255,255,255,.08)}
.toc a.active{border-color:rgba(255,255,255,.25);background:linear-gradient(90deg, rgba(96,165,250,.28), rgba(34,211,238,.28))}
.share .share-row{display:grid;gap:8px}
.share button{display:flex;align-items:center;gap:8px;padding:8px 10px;border-radius:12px;border:1px solid var(--glass-stroke);background:rgba(255,255,255,.06);color:var(--text);cursor:pointer}
.share button:hover{background:rgba(255,255,255,.12)}
.author .author-row{display:flex;gap:8px;align-items:center}
.author img{width:40px;height:40px;border-radius:50%;object-fit:cover}
.author .name{font-weight:700}
.author-meta{opacity:.85;margin-top:6px}

/* article */
article{max-width:760px;margin:0 auto 36px}
.lede{font-size:1.1rem;opacity:.98}
h2{font-size:1.6rem;margin:28px 0 12px;font-weight:900}
h3{font-size:1.15rem;margin:14px 0 6px;font-weight:800}
p{line-height:1.8}
ul{padding-left:18px}
.grid-2{display:grid;grid-template-columns:1fr 1fr;gap:14px}
.check li::marker{content:"✔  "}
.no li::marker{content:"✖  "}
.steps{counter-reset:step}
.steps>li{margin-bottom:8px}
.steps>li::marker{font-weight:800}

/* callout */
.callout{border-radius:16px;padding:14px;border:1px solid var(--glass-stroke);background:rgba(255,255,255,.06);margin:14px 0}
.callout.info{background:linear-gradient(180deg, rgba(96,165,250,.15), rgba(255,255,255,.06))}
.callout.tip{background:linear-gradient(180deg, rgba(34,211,238,.15), rgba(255,255,255,.06))}
.callout.success{background:linear-gradient(180deg, rgba(52,211,153,.18), rgba(255,255,255,.06))}

/* template box */
.template{border:1px solid var(--glass-stroke);border-radius:16px;overflow:hidden;background:rgba(255,255,255,.04);margin-top:8px}
.template pre{margin:0;padding:14px;line-height:1.7;white-space:pre-wrap}
.template-actions{display:flex;gap:10px;align-items:center;padding:10px;border-top:1px solid var(--glass-stroke)}
.template-actions .ghost{color:var(--text);opacity:.9;text-decoration:none}
.template-actions button{display:flex;align-items:center;gap:8px;padding:8px 10px;border-radius:12px;border:1px solid var(--glass-stroke);background:rgba(255,255,255,.08);color:var(--text);cursor:pointer}
.template-actions button:hover{background:rgba(255,255,255,.14)}

/* details/faq */
details{background:rgba(255,255,255,.05);border:1px solid var(--glass-stroke);border-radius:14px;padding:10px 12px;margin:8px 0}
summary{cursor:pointer;font-weight:700}

/* end actions */
.end-actions{display:flex;gap:10px;align-items:center;margin:18px 0 6px}
.btn-primary{display:inline-flex;align-items:center;gap:10px;padding:12px 16px;border-radius:999px;color:#fff;text-decoration:none;
  background:linear-gradient(90deg,var(--p1),var(--p2));border:1px solid rgba(255,255,255,.25)}
.ghost{padding:10px 12px;border-radius:12px;border:1px solid var(--glass-stroke);text-decoration:none;color:var(--text);background:rgba(255,255,255,.06)}

/* related */
.related{border-top:1px solid rgba(255,255,255,.08);padding-top:20px}
.related h3{font-size:1.2rem;margin:0 0 12px}
.rel-grid{display:grid;grid-template-columns:repeat(3,1fr);gap:14px}
.rel-card{position:relative;overflow:hidden;border-radius:16px;border:1px solid rgba(255,255,255,.12);text-decoration:none;color:#fff;background:#0f1629}
.rel-card img{position:absolute;inset:0;width:100%;height:100%;object-fit:cover;filter:brightness(.9);transform:scale(1.02);transition:transform .5s}
.rel-card:hover img{transform:scale(1.06)}
.rel-card .meta{position:relative;inset:auto;padding:12px;background:linear-gradient(180deg, rgba(0,0,0,.0), rgba(0,0,0,.75));margin-top:110px}
.rel-card .cat{display:inline-block;padding:6px 10px;border-radius:999px;background:rgba(255,255,255,.16);border:1px solid rgba(255,255,255,.28);margin-bottom:6px}
.rel-card .title{font-weight:800}

/* responsive */
@media (max-width: 1024px){
  .containerr{grid-template-columns:1fr}
  .side{order:2}
  article{max-width:680px}
}
@media (max-width:640px){
  .hero{height:54vh}
  .grid-2{grid-template-columns:1fr}
  .rel-grid{grid-template-columns:1fr}
}
    `}</style>
  );
}
